import styled from 'styled-components';
import BottomModal from '../../../components/modal/BottomModal';
import { useEffect, useState } from 'react';

interface Props {
  usage: string;
  setUsage: (usage: string) => void;
  usageDetail: string;
  setUsageDetail: (usageDetail: string) => void;
  onClose: () => void;
  useTypeList?: boolean;
}

const typeList = [
  {
    category: '주거용',
    details: [
      '아파트',
      '단독/연립',
      '다가구/원룸',
      '다세대/빌라',
      '근린/상가주택',
      '오피스텔',
      '주거 기타',
    ],
  },
  {
    category: '상업/산업용',
    details: [
      '근린생활시설',
      '사무실',
      '지식산업센터',
      '창고',
      '공장',
      '숙박시설',
      '숙박 (콘도 등)',
      '목욕탕',
      '노유자시설',
      '문화 및 집회시설',
      '종교시설',
      '의료시설',
      '교육연구시설',
      '장례관련시설',
      '농가관련시설',
      '주유소',
      '자동차관련시설',
      '분뇨 및 쓰레기처리',
      '상업 기타',
    ],
  },
  {
    category: '토지',
    details: [
      '대지',
      '농지(전답)',
      '과수원',
      '임야',
      '잡종지',
      '도로',
      '주차장',
      '묘지',
      '목장용지',
      '공장용지',
      '창고용지',
      '기타용지',
      '토지 기타',
      '기타',
    ],
  },
];

const list = [
  {
    category: '주거용',
    details: [
      '아파트',
      '주택',
      '다세대(빌라)',
      '다가구(원룸등)',
      '오피스텔',
      '근린주택',
    ],
  },
  {
    category: '상업/산업용',
    details: [
      '근린상가',
      '근린시설',
      '사무실',
      '공장',
      '창고',
      '아파트형공장',
      '숙박(콘도등)',
      '주유소(위험물)',
      '교육시설',
      '숙박시설',
      '의료시설',
      '종교시설',
      '노유자시설',
      '농가관련시설',
      '장례관련시설',
      '문화및집회시설',
      '자동차관련시설',
      '분뇨쓰레기처리',
    ],
  },
  {
    category: '토지',
    details: [
      '농지',
      '대지',
      '임야',
      '과수원',
      '잡종지',
      '도로',
      '주차장',
      '묘지',
      '공장용지',
      '창고용지',
      '목장용지',
      '기타용지',
    ],
  },
];

function UsageModal({
  usage,
  setUsage,
  usageDetail,
  setUsageDetail,
  onClose,
  useTypeList = false,
}: Props) {
  const [localUsage, setLocalUsage] = useState(usage);
  const [localDetailUsage, setLocalDetailUsage] = useState(usageDetail);
  const [openOptionList, setOpenOptionList] = useState<string[]>([usage]);

  const usageData = useTypeList ? typeList : list;

  const handleConfirm = () => {
    setUsage(localUsage);
    setUsageDetail(localDetailUsage);
    onClose();
  };

  const toggleCategory = (r: string) => {
    if (openOptionList.includes(r)) {
      setOpenOptionList(openOptionList.filter((i) => i !== r));
    } else {
      setOpenOptionList([...openOptionList, r]);
    }
  };

  const handleDetailItemClick = (category: string, item: string) => {
    setLocalUsage(category);
    if (item === '전체') {
      setLocalDetailUsage('');
    } else {
      setLocalDetailUsage(item);
    }
  };

  const isAllSelected =
    (localUsage === '' && localDetailUsage === '') ||
    usage === '' ||
    usage === '' ||
    usage === '전체';

  return (
    <BottomModal onClose={onClose}>
      <Header>용도 선택</Header>
      <Content>
        <Item
          onClick={() => {
            setLocalUsage('');
            setLocalDetailUsage('');
          }}
        >
          <ItemLabel $active={localUsage === '' && localDetailUsage === ''}>
            전체
          </ItemLabel>
          {isAllSelected && (
            <img src="/assets/images/check/npl-blue-check.svg" alt="active" />
          )}
        </Item>
        {usageData.map(({ category, details }) => (
          <>
            <DropdownRegion onClick={() => toggleCategory(category)}>
              <DropdownRegionLabel>{category}</DropdownRegionLabel>
              <DropdownArrow
                src="/assets/images/arrow/arrow-right-tertiary.svg"
                alt=""
                $open={openOptionList.includes(category)}
              />
            </DropdownRegion>
            {openOptionList.includes(category) && (
              <>
                <Item onClick={() => handleDetailItemClick(category, '전체')}>
                  <ItemLabel
                    $active={localUsage === category && localDetailUsage === ''}
                  >
                    {category} 전체
                  </ItemLabel>
                  {localUsage === category && localDetailUsage === '' && (
                    <img
                      src="/assets/images/check/npl-blue-check.svg"
                      alt="active"
                    />
                  )}
                </Item>
                {details.map((c) => (
                  <Item
                    key={c}
                    onClick={() => handleDetailItemClick(category, c)}
                  >
                    <ItemLabel $active={localDetailUsage === c}>{c}</ItemLabel>
                    {localDetailUsage === c && (
                      <img
                        src="/assets/images/check/npl-blue-check.svg"
                        alt="active"
                      />
                    )}
                  </Item>
                ))}
              </>
            )}
          </>
        ))}
      </Content>
      <Bottom>
        <CancleButton onClick={onClose}>취소</CancleButton>
        <ConfirmButton onClick={handleConfirm}>적용</ConfirmButton>
      </Bottom>
    </BottomModal>
  );
}

export default UsageModal;

const Header = styled.div`
  display: flex;
  padding: 24px 20px 8px 20px;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;

  color: #0c0e12;
  font-size: 18px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.36px;
`;

const Content = styled.div`
  display: flex;
  height: 100%;
  padding: 8px 20px 24px 20px;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  overflow-y: auto;
`;

const Item = styled.div`
  display: flex;
  padding: 4px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
`;

const ItemLabel = styled.div<{ $active?: boolean }>`
  display: flex;
  padding: 8px;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
  color: ${({ $active }) => ($active ? '#0c0e12' : '#787D87')};
  font-size: 16px;
  font-weight: 500;
  line-height: 24px; /* 150% */
  letter-spacing: -0.32px;
`;

const DropdownRegion = styled.div`
  display: flex;
  padding: 4px;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  border-radius: 8px;
  background: #f4f5f7;
`;

const DropdownArrow = styled.img<{ $open?: boolean }>`
  width: 20px;
  height: 20px;
  transform: ${({ $open }) => ($open ? 'rotate(-90deg)' : 'rotate(90deg)')};
`;

const DropdownRegionLabel = styled.div`
  display: flex;
  padding: 8px;
  align-items: center;
  flex: 1 0 0;
  color: #424854;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px; /* 150% */
  letter-spacing: -0.32px;
`;

const Bottom = styled.div`
  display: flex;
  align-self: stretch;
  padding: 16px 24px 24px 24px;
  align-items: center;
  gap: 8px;
`;

const Button = styled.button`
  border: none;
  outline: none;

  display: flex;
  height: 44px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;

  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: -0.3px;
`;

const CancleButton = styled(Button)`
  background: #f4f5f7;
  color: #424854;
`;

const ConfirmButton = styled(Button)`
  background: #3f6ef1;
  color: rgba(244, 245, 247, 0.88);
  flex: 1 0 0;
`;
